import { useEffect, useMemo } from "react";

import { useLocalization } from "@fluent/react";
import {
  onboarding,
  translatePromptConfiguration,
  useObjectMemo,
} from "byzantine";

import { useUserFeatures } from "../../../../../components/contexts/UserFeaturesContext";
import { useCurrentUser } from "../../../../../components/contexts/CurrentUserContext";
import {
  useOnboardingGuideStorage,
  guideActions,
} from "../../../guides/storage";

import { useDismissAnimation } from "./useDismissAnimation";
import { useGuideCompletionAnimation } from "./useGuideCompletionAnimation";

const { useOnboardingContainer } = onboarding;

export const useExistingUserPrompts = () => {
  const { l10n } = useLocalization();
  const { user_guide_type: userGuideType } = useUserFeatures();
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.id?.toString() || "";

  const [guideState, setGuideState] = useOnboardingGuideStorage(userId);

  const isExistingGuide = userGuideType === "existing";

  const {
    prompts: rawPrompts,
    fetchOnboardingPrompts,
    loading,
  } = useOnboardingContainer("web");

  const { dismissingUuid, dismissSpring, dismissPrompt } =
    useDismissAnimation();

  useEffect(() => {
    if (isExistingGuide) {
      fetchOnboardingPrompts();
    }
  }, [isExistingGuide, fetchOnboardingPrompts]);

  const translatedPrompts = useMemo(
    () =>
      rawPrompts.map((prompt) => translatePromptConfiguration(prompt, l10n)),
    [rawPrompts, l10n]
  );

  const visiblePrompts = useGuideCompletionAnimation({
    translatedPrompts,
    rawPrompts,
    fetchOnboardingPrompts,
    dismissingUuid,
    dismissPrompt,
  });

  // Reset caughtUpDismissed when real prompts become visible again
  useEffect(() => {
    if (visiblePrompts.length > 0 && guideState.caughtUpDismissed) {
      setGuideState(guideActions.resetCaughtUpDismissed);
    }
  }, [visiblePrompts.length, guideState.caughtUpDismissed, setGuideState]);

  return useObjectMemo({
    isExistingGuide,
    isLoading: loading,
    rawPrompts,
    visiblePrompts,
    fetchOnboardingPrompts,
    guideState,
    setGuideState,
    dismissingUuid,
    dismissSpring,
    dismissPrompt,
  });
};
